import React,
{
    FC, memo, useEffect, useMemo, useState,
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DateTime} from 'luxon';
import Box from '@mui/material/Box';
import Dialog, {DialogProps} from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Grid from "@mui/material/Grid";
import {SelectChangeEvent} from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import CloseIcon from '@mui/icons-material/Close';
import {useAppInsightsContext} from "@microsoft/applicationinsights-react-js";
import {Alert, Button, Spinner} from 'react-bootstrap';
import {useLocation} from 'react-router-dom';
import Constants, {ASAP_TIME, Routes, WEEK_DAYS} from "app/shared/constants/constants";
import {
    formatDateLabel, formatTimeLabel, getPickUpDescription, isTodayDate,
} from 'app/shared/utils/utilityFunctions';
import {
    resetStore,
    selectPickUpType,
    updatePickUpDate,
    updatePickUpTime,
    updateStoreEligibility,
    updateStoreStatus,
} from 'app/store/slices/storeSlice';
import {RootState} from 'app/store';
import ARIA_LABELS from 'constants/ariaLabels';
import LABELS from 'app/shared/constants/Literals';
import logging from 'app/shared/utils/logging';
import {isRewardExpired} from 'app/features/RewardsHome/RewardsUtility';
import {setHasRewaredSelected, updateSelectedReward} from 'app/store/slices/rewardsSlice';
import {RewardsDetails} from 'app/features/Rewards/types/RewardsDetails';
import {Hours} from 'app/features/Location/types/YextLocationResponseType';
import {mapStoreHours} from 'app/shared/utils/helperFunctions';
import useStoreAvailability from "../hooks/useStoreAvailability";
import StyledPickupSelect from './StyledPickupSelect';
import useAppUtil from './useAppUtil';

interface PickupDialogProps extends Omit<DialogProps, 'onChange' | 'onClose'> {
    storeId: string;
    pickUpDate: string,
    pickUpTime: string,
    pickUpType: string,
    open: boolean;
    onChange: (selectedPickupDate: string) => void;
    onClose?: () => void;
    showCloseButton?: boolean
}

const PickupModal: FC<PickupDialogProps> = (props) => {
    const dispatch = useDispatch();
    const appInsights = useAppInsightsContext();
    appInsights.trackPageView({
        name: "PickUpSelections",
    });
    const metricData = {
        average: 5,
        name: "React Component Engaged Time (seconds)",
        sampleCount: 1,
    };
    const additionalProperties = {"Component Name": 'PickupModal'};
    appInsights.trackMetric(metricData, additionalProperties);
    const { storeDescription, storeNumber } = useSelector((state: RootState) => state.stores);
    const [displayCloseBtn, setDisplayCloseBtn] = useState<boolean>(true);
    const {
        hasRewaredSelected,
        selectedReward,
    } = useSelector((state: RootState) => state.rewards);
    const { pathname } = useLocation();
    const {
        open,
        onClose = () => {},
        storeId,
        onChange,
        pickUpDate,
        pickUpTime,
        pickUpType,
        showCloseButton = true,
    } = props;

    const initialPickupValue = {
        pickupDate: '',
        pickupTime: '',
        pickupType: '',
    };
    const {
        isFetching,
        isEligible,
        pickupTypeOptions = [],
        address,
        storeHours,
        timezone = '',
        availability = [],
        isClosedToday = false,
        isError,
        cOrderAheadTempOff = false,
        cOrderAhead = true,
        closed = false,
    } = useStoreAvailability(storeId);
    const [pickupValue, setPickupValue] = useState(initialPickupValue);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isForLater, setIsForLater] = useState(false);
    const [selectedStore, setSelectedStore] = useState(storeId);
    // Determine if pickup is available:
    // - must have pickup type options
    // - "order ahead" not temporarily turned off
    // - store not closed
    // - "order ahead" enabled
    const isPickupAvailable = pickupTypeOptions.length > 0
            && !cOrderAheadTempOff
            && !closed
            && cOrderAhead;
    const isStoreClosed = closed;
    const {navigateHome} = useAppUtil();

    useEffect(() => {
        if (storeId !== selectedStore) {
            setSelectedStore(storeId);
            setIsUpdating(false);
            setIsForLater(false);
            setPickupValue(initialPickupValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, selectedStore]);

    useEffect(() => {
        if (pickUpType !== Constants.NO_PICKUP_TYPE && pickUpType !== '') {
            setIsUpdating(true);
            setIsForLater(true);
        } else {
            setIsUpdating(false);
            setIsForLater(false);
        }
        setPickupValue({
            pickupDate: (pickUpDate !== 'now' && pickUpDate?.split(' ')[0]) || '',
            pickupTime: (pickUpTime !== Constants.NO_PICKUP_TIME && pickUpTime) || ASAP_TIME,
            pickupType: (pickUpType !== Constants.NO_PICKUP_TYPE && pickUpType) || '',
        });
    }, [pickUpDate, pickUpTime, pickUpType, open]);

    useEffect(() => {
        if (!showCloseButton) {
            setDisplayCloseBtn(false);
        } else {
            const hideButton = (pickUpType === "" && pickUpTime === "")
            || (pickUpTime === Constants.NO_PICKUP_TIME && pickUpType === Constants.NO_PICKUP_TYPE && storeNumber !== "")
            || (pickUpTime === "" && pickUpType === ASAP_TIME && storeNumber !== "")
            || !isUpdating;
            setDisplayCloseBtn(!hideButton);
        }
    }, [pickUpType, pickUpTime, pickUpDate, storeNumber, showCloseButton, isUpdating]);

    const handleChange = (e: SelectChangeEvent<unknown>) => {
        // eslint-disable-next-line
        const { name = '', value } = e.target;
        setPickupValue((prev) => ({
          ...prev,
          [name]: value as string,
          ...(name === 'pickupDate' ? { pickupTime: '' } : {}),
        }));
      };
    if (isError) {
        logging.error(appInsights, "evt_LocationStorePage_err", "Error getting store availability");
    }

    const handleClose = () => {
        if (!displayCloseBtn) {
            return;
        }
        // If the user is updating the pickup date and time, in rewards page,
        // and the user dismisses the modal, reset the selected reward
        if (
            pathname === Routes.REWARDS
            && isUpdating
            && hasRewaredSelected && isRewardExpired(selectedReward, pickUpDate)
        ) {
            dispatch(setHasRewaredSelected(false));
            dispatch(updateSelectedReward({} as RewardsDetails));
        }
        onClose();
    };
    /**
     * Handles store closure event.
     * - Navigates user back to the home page
     * - Resets store state in Redux
     * - Triggers parent dialog/overlay close
     */
    const handleStoreClose = () => {
        navigateHome();
        dispatch(resetStore());
        onClose();
    };

    const handleBrowse = (isASAP = false) => {
        const currentTime = DateTime.local()?.setZone(timezone);
        const isASAPOrder = isASAP || pickupValue.pickupTime.includes(ASAP_TIME);
        const pickupType = isPickupAvailable
            ? pickupValue.pickupType
            : Constants.NO_PICKUP_TYPE;
        dispatch(selectPickUpType(pickupType));
        dispatch(updatePickUpTime({pickupTime: isASAP ? ASAP_TIME : pickupValue.pickupTime}));

        const userSelectedDate = isASAPOrder
            ? currentTime
            : DateTime.fromFormat(`${pickupValue.pickupDate} ${pickupValue.pickupTime}`, 'yyyy-MM-dd h:mm a', {zone: timezone});

        const dateString = `${userSelectedDate.toFormat("yyyy-MM-dd HH:mm:ss")} UTC`;
        const diff = isASAPOrder ? 0 : userSelectedDate.diff(currentTime, 'minutes').minutes;
        if (isEligible && isPickupAvailable) {
            dispatch(updateStoreEligibility(true));
        } else {
            dispatch(updateStoreEligibility(false));
        }

        dispatch(updatePickUpDate({pickupDate: dateString, scheduleIn: diff}));
        onChange(pickupValue.pickupDate);
        onClose();
    };

    useEffect(() => {
        if (pickupTypeOptions.length > 0 && !pickupValue.pickupType) {
            setPickupValue((prev) => ({
                ...prev,
                pickupType: pickupTypeOptions?.[0],
            }));
        }
    }, [pickupTypeOptions, pickupValue.pickupType]);

    useEffect(() => {
        dispatch(updateStoreStatus(isStoreClosed));
    }, [isStoreClosed, dispatch]);

    const pickupTimeOptions = useMemo(() => {
        const selectedDate = availability?.find(
            // eslint-disable-next-line max-len
            (option: { date: string; pickupTimes?: string[] }) => option.date === pickupValue.pickupDate,
        );

        const asapOption = isTodayDate(selectedDate?.date ?? '', timezone) ? ASAP_TIME : undefined;

        const times = selectedDate?.pickupTimes?.map((time) => formatTimeLabel(time)) ?? [];

        return asapOption && !isClosedToday
            ? [asapOption, ...times]
            : times;
    }, [availability, isClosedToday, pickupValue.pickupDate, timezone]);

    const pickupDateOptions = useMemo(
        () => availability
            ?.filter((option) => option?.pickupTimes?.length > 0)
            .map((option) => option.date) ?? [],
        [availability],
    );

    const loadingSpinner = (
        <Box className="loading-availability">
            <Spinner animation="border" variant="secondary" role="status"/>
        </Box>
    );

    const title = `${isUpdating ? 'Update' : 'Choose'} your pickup  ${isForLater ? 'date' : 'type'}:`;
    let CTAButtonText;
    if (isStoreClosed) {
        CTAButtonText = 'Close';
    } else if (isUpdating) {
        CTAButtonText = 'Update';
    } else if (pathname === Routes.HOME && hasRewaredSelected && pickUpDate === 'now') {
        // If the user selected a reward but three is no pickup date, show the "Continue" button
        CTAButtonText = 'Continue';
    } else {
        CTAButtonText = 'Browse Menu';
    }

    const storeHoursText = useMemo(() => {
        if (!storeHours) return '';
        const selectedDate = (!pickupValue.pickupDate || pickupValue.pickupDate === Constants.NOW)
                                ? DateTime.now().toFormat("yyyy-MM-dd")
                                : DateTime.fromISO(pickupValue.pickupDate).toFormat("yyyy-MM-dd");

        const dayOfWeek = (WEEK_DAYS as (keyof Omit<Hours, "holidayHours">)[])[new Date(`${selectedDate}T00:00:00Z`).getUTCDay()];
        let selectedDateHours = storeHours?.[dayOfWeek]?.openIntervals?.[0];
        const {holidayHours: tempHolidayHours = []} = storeHours;
        const holidayHour = tempHolidayHours.find((holiday) => holiday.date === selectedDate);
        if (holidayHour) {
            selectedDateHours = holidayHour.openIntervals?.[0];
        }

        if (!selectedDateHours) return `Closed ${formatDateLabel(selectedDate, false)}`; // Handle case where store is closed

        // Inline time formatting using Intl.DateTimeFormat
        const formatTime = (time: string) => {
            const [hour, minute] = time.split(":").map(Number);
            return new Intl.DateTimeFormat("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            }).format(new Date(2000, 0, 1, hour, minute));
        };

        return `Store hours ${formatDateLabel(selectedDate, false)} - ${formatTime(selectedDateHours.start)} to ${formatTime(selectedDateHours.end)} ${mapStoreHours(timezone || '')}`;
    }, [storeHours, timezone, pickupValue.pickupDate]);

    const renderPickupType = (
        <>
            <StyledPickupSelect
                onChange={handleChange}
                name="pickupType"
                options={pickupTypeOptions}
                value={pickupValue.pickupType}
                id="select-pickup-type"
            />

            {pickupValue.pickupType && (
                <Alert className="pickup-info font-sub" aria-live="polite" role="alert">
                    {getPickUpDescription(pickupValue.pickupType)}
                </Alert>
            )}
        </>
    );

    const renderPickupDateAndTime = (
        <div className="render-pickup-date-time">
            {isUpdating && (
                <div className="render-pickup-select">
                    <StyledPickupSelect
                        onChange={handleChange}
                        name="pickupType"
                        options={pickupTypeOptions}
                        value={pickupValue.pickupType}
                        id="select-pickup-type"
                    />
                </div>
            )}
            <div className="render-pickup-select">
                <StyledPickupSelect
                    onChange={handleChange}
                    name="pickupDate"
                    options={pickupDateOptions}
                    value={pickupValue.pickupDate}
                    labelFormater={formatDateLabel}
                    id="select-pickup-date"
                />

                {pickupValue.pickupDate && (
                    <StyledPickupSelect
                        onChange={handleChange}
                        name="pickupTime"
                        options={pickupTimeOptions}
                        value={pickupValue.pickupTime}
                        id="select-pickup-time"
                    />
                )}
            </div>
        </div>
    );

    const renderStoreAddress = address && (
        <Grid
            item
            xs={12}
            className="pickup-store-address"
            role="region"
            aria-labelledby="store-address-heading"
        >
            <Typography
                id="store-address-heading"
                className="font-main"
                component="h2"
                variant="h6"
                hidden
            >
                Store Address
            </Typography>
            <Typography className="font-main" component="p" aria-live="polite">{storeDescription}</Typography>
            <Typography className="font-sub" component="p" aria-live="polite">{address.line1}</Typography>
            {address.line2 && (
                <Typography className="font-sub" component="p" aria-live="polite">
                    {address.line2}
                </Typography>
            )}
            <Typography className="font-sub" component="p" aria-live="polite">
                {`${address.city}, ${address.region} ${address.countryCode} ${address.postalCode}`}
            </Typography>
        </Grid>
    );

    const renderStoreHoursToday = (
        <Grid
            item
            xs={12}
            className="pickup-store-hours"
            role="region"
            aria-labelledby="store-hours-heading"
        >
            <Typography
                id="store-hours-heading"
                className="font-main"
                component="h2"
                variant="h6"
                hidden
            >
                Store Hours
            </Typography>
            <Typography className="font-sub" component="p" aria-live="polite">
                {storeHoursText}
            </Typography>
        </Grid>
    );

    const renderErrorMessage = (() => {
        if (closed) {
            return (
                <Alert className="pickup-info font-sub error-main" aria-live="polite" role="alert">
                    {LABELS.FUTURE_ORDERS.storeClosed}
                </Alert>
            );
        }

        if (isPickupAvailable && isClosedToday && !isForLater) {
            return (
                <Alert className="pickup-info font-sub error-main" aria-live="polite" role="alert">
                    {LABELS.FUTURE_ORDERS.pickUpNotAvailable}
                    <br />
                    {LABELS.FUTURE_ORDERS.clickLater}
                </Alert>
            );
        }

        if (!isPickupAvailable) {
            return (
                <Alert className="pickup-info font-sub error-main" aria-live="polite" role="alert" data-testid="pickup-unavailable-alert">
                    {LABELS.FUTURE_ORDERS.pickUpNotAvailable}
                    <br />
                    {LABELS.FUTURE_ORDERS.clickBrowse}
                </Alert>
            );
        }

        return null;
    })();

    const renderActionButtons = () => {
        if (isStoreClosed) {
            return (
                <Button
                    style={!isUpdating ? { width: 125 } : {}}
                    className="order-now-card-button"
                    aria-label={CTAButtonText}
                    onClick={() => handleStoreClose()}
                >
                    {CTAButtonText}
                </Button>
            );
        }
        if (isForLater) {
            return (
                <Button
                    style={!isUpdating ? { width: 125 } : {}}
                    className="order-now-card-button"
                    aria-label={CTAButtonText}
                    disabled={
                        !pickupValue.pickupDate
                        || !pickupValue.pickupTime
                    }
                    onClick={() => handleBrowse()}
                >
                    {CTAButtonText}
                </Button>
            );
        }
        return (
            <Box className="action-buttons-center">
                <Button
                    aria-label={ARIA_LABELS.PICKUP_MODAL.selectASAP}
                    className="order-now-card-button"
                    disabled={isFetching || !isEligible || isClosedToday}
                    hidden={!isPickupAvailable}
                    onClick={() => handleBrowse(true)}
                >
                    {LABELS.FUTURE_ORDERS.asap}
                </Button>
                {isPickupAvailable
                    ? (
                        <Button
                            aria-label={ARIA_LABELS.PICKUP_MODAL.selectLater}
                            className="order-now-card-button"
                            disabled={isFetching || !isEligible}
                            onClick={() => setIsForLater(true)}
                        >
                            {LABELS.FUTURE_ORDERS.later}
                        </Button>
                    ) : (
                        <Button
                            aria-label={CTAButtonText}
                            className="order-now-card-button"
                            disabled={isFetching || (!isEligible && !isError)}
                            onClick={() => handleBrowse()}
                        >
                            {CTAButtonText}
                        </Button>
                    )}
            </Box>
        );
    };

    return (
        <Dialog
            className="pickup-date-time-dialog"
            disableEscapeKeyDown={!displayCloseBtn}
            open={open}
            onClose={handleClose}
            aria-labelledby="pickup-modal-title"
            aria-describedby="pickup-modal-description"
            slotProps={{
                paper: {
                  sx: {
                    margin: '0.5rem',
                    borderRadius: '.5rem',
                  },
                  role: 'dialog',
                },
            }}
        >
            <DialogTitle className="pickup-date-time-dialog-title font-main font-header" id="pickup-modal-title">
                {title}
                { displayCloseBtn && (
                    <IconButton
                        size="small"
                        className="pickup-date-time-close-btn"
                        aria-label={ARIA_LABELS.PICKUP_MODAL.close}
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </IconButton>
                ) }
            </DialogTitle>
            <DialogContent id="pickup-modal-description" className="pickup-date-time-dialog-content">
                {isFetching ? loadingSpinner : (
                    <>
                        {isForLater ? renderPickupDateAndTime : renderPickupType}
                        {renderStoreAddress}
                        {renderStoreHoursToday}
                        {renderErrorMessage}
                        {(!isForLater && isPickupAvailable) && (
                            <Typography
                                className="font-main"
                                component="h2"
                            >
                                {LABELS.FUTURE_ORDERS.pickupQuestion}
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions className={`date-time-dialog-actions ${isForLater || isUpdating ? 'bordered-dialog-actions' : ''}`}>
                {renderActionButtons()}
            </DialogActions>
        </Dialog>
    );
};

export default memo(PickupModal);
